import { useMemo, useState } from "react";
import type { Address, Hash } from "viem";
import { concatHex, encodeDeployData, encodeFunctionData, isAddress, parseEther, toHex } from "viem";
import { useAccount, useChainId, useConnect, useDisconnect, usePublicClient, useSwitchChain, useWalletClient } from "wagmi";
import { base, baseSepolia, mainnet, sepolia } from "wagmi/chains";
import { explorerAddressUrl, explorerTxUrl } from "../lib/explorer";
import { AttackerArtifact, AttackerNoParamsArtifact, ContractSources, DummyTokenArtifact, VulnerableLenderPoolArtifact } from "../contracts/workshopArtifacts";

const SCENARIO1_CHAIN = sepolia;

function shortAddr(a?: string) {
  if (!a) return "";
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}

function parseError(e: unknown) {
  if (e instanceof Error) return e.message;
  return String(e);
}

export function App() {
  // --- Password gate (client-side only) ---
  // For a stronger protection (real auth), use Cloudflare Access in front of Pages.
  const requiredPassword = ((import.meta as any).env?.VITE_DAPP_PASSWORD as string | undefined) ?? "bny_test_1231";
  const [isAuthed, setIsAuthed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem("bny_workshop_authed") === "1";
    } catch {
      return false;
    }
  });
  const [passwordInput, setPasswordInput] = useState<string>("");

  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { connectors, connectAsync, isPending: isConnecting } = useConnect();
  const { disconnect } = useDisconnect();
  const { switchChainAsync, isPending: isSwitching } = useSwitchChain();
  const testnetPublicClient = usePublicClient({ chainId: SCENARIO1_CHAIN.id });
  // Bind the wallet client to the same chainId we’re about to send txs on.
  // Otherwise viem will reject with "current chain does not match target chain".
  const { data: walletClient } = useWalletClient({ chainId: SCENARIO1_CHAIN.id });
  const basePublicClient = usePublicClient({ chainId: base.id });
  const { data: baseWalletClient } = useWalletClient({ chainId: base.id });

  const currentChain = useMemo(() => {
    return [mainnet, base, sepolia, baseSepolia].find((c) => c.id === chainId);
  }, [chainId]);

  const chainForLinks = useMemo(() => {
    return SCENARIO1_CHAIN ?? currentChain;
  }, [currentChain]);

  const coinbaseConnector = useMemo(() => {
    return connectors.find((c) => c.id === "coinbaseWalletSDK") ?? connectors[0] ?? null;
  }, [connectors]);

  const [log, setLog] = useState<string>("");
  const pushLog = (line: string) => setLog((l) => `${new Date().toISOString()}  ${line}\n${l}`);

  const [tokenAddress, setTokenAddress] = useState<Address | undefined>(undefined);
  const [tokenDeployTx, setTokenDeployTx] = useState<Hash | undefined>(undefined);
  const [poolAddress, setPoolAddress] = useState<Address | undefined>(undefined);
  const [poolDeployTx, setPoolDeployTx] = useState<Hash | undefined>(undefined);
  const [attackerAddress, setAttackerAddress] = useState<Address | undefined>(undefined);
  const [attackerDeployTx, setAttackerDeployTx] = useState<Hash | undefined>(undefined);
  const [attackTx, setAttackTx] = useState<Hash | undefined>(undefined);

  // Scenario 2 (Base): fixed addresses + attacker deployment
  const SCENARIO2_TOKEN = "0x81f15786747b005272f897215442179e4e0dc1ff" as Address;
  const SCENARIO2_VPOOL = "0xB9a52cdD11Ca8786b22C63159501557a9dAd9306" as Address;
  const MULTICALL3_ADDRESS = "0xcA11bde05977b3631167028862bE2a173976CA11" as Address;
  const SINGLETON_FACTORY_ADDRESS = "0x4e59b44847b379578588920cA78FbF26c0B4956C" as Address;
  const [s2AttackerDeployTx, setS2AttackerDeployTx] = useState<Hash | undefined>(undefined);
  const [s2AttackTx, setS2AttackTx] = useState<Hash | undefined>(undefined);
  const [s2AttackerAddressInput, setS2AttackerAddressInput] = useState<string>("");

  // Wallet separation guard
  const [step1Deployer, setStep1Deployer] = useState<Address | undefined>(undefined);
  const [step2Deployer, setStep2Deployer] = useState<Address | undefined>(undefined);

  // Code modal
  const [showCode, setShowCode] = useState(false);
  const [codeTab, setCodeTab] = useState<"token" | "vpool" | "attacker">("token");

  async function ensureChain(targetChainId: number) {
    if (!isConnected) throw new Error("Connect Coinbase Wallet first");
    if (chainId !== targetChainId) {
      await switchChainAsync({ chainId: targetChainId });
      // React needs a tick to re-bind `useWalletClient({ chainId })`.
      // Tell the user to click again instead of failing with a confusing chain mismatch.
      throw new Error("Switched network in wallet. Click the same button again to continue.");
    }
    if (!walletClient) throw new Error("Wallet client not ready for this network (try again)");
  }

  async function ensureBaseChain() {
    if (!isConnected) throw new Error("Connect Coinbase Wallet first");
    if (chainId !== base.id) {
      await switchChainAsync({ chainId: base.id });
      throw new Error("Switched network to Base. Click the same button again to continue.");
    }
    if (!baseWalletClient) throw new Error("Wallet client not ready for Base (try again)");
  }

  async function onConnectCoinbase() {
    if (!coinbaseConnector) throw new Error("No wallet connector available");
    await connectAsync({ connector: coinbaseConnector });
  }

  async function requireDifferentWalletThanStep1() {
    if (!address) throw new Error("No connected address");
    if (!step1Deployer) throw new Error("Run step 1 first");
    if (address.toLowerCase() === step1Deployer.toLowerCase()) {
      throw new Error("Step 2/3 must be sent from a DIFFERENT wallet address. Switch Coinbase Wallet account, then retry.");
    }
  }

  async function onStep1DeployTokenAndPool() {
    await ensureChain(SCENARIO1_CHAIN.id);
    if (!walletClient) throw new Error("Wallet client not ready");
    const client = testnetPublicClient;
    if (!client) throw new Error("Public client not ready (testnet)");
    if (!address) throw new Error("No address");

    pushLog(`Step 1: Deploying DummyToken + VulnerableLenderPool on ${SCENARIO1_CHAIN.name}…`);
    setStep1Deployer(address as Address);

    // 1) Deploy token(name,symbol)
    const tokenHash = await walletClient.deployContract({
      abi: DummyTokenArtifact.abi,
      bytecode: DummyTokenArtifact.bytecode,
      args: ["DummyToken", "DUMMY"]
    });
    setTokenDeployTx(tokenHash);
    pushLog(`Token deploy tx sent: ${tokenHash}`);
    const tokenReceipt = await client.waitForTransactionReceipt({ hash: tokenHash });
    if (!tokenReceipt.contractAddress) throw new Error("No token contractAddress in receipt");
    setTokenAddress(tokenReceipt.contractAddress);
    pushLog(`Token deployed: ${tokenReceipt.contractAddress}`);

    // 2) Deploy vPool(token)
    const poolHash = await walletClient.deployContract({
      abi: VulnerableLenderPoolArtifact.abi,
      bytecode: VulnerableLenderPoolArtifact.bytecode,
      args: [tokenReceipt.contractAddress]
    });
    setPoolDeployTx(poolHash);
    pushLog(`vPool deploy tx sent: ${poolHash}`);
    const poolReceipt = await client.waitForTransactionReceipt({ hash: poolHash });
    if (!poolReceipt.contractAddress) throw new Error("No vPool contractAddress in receipt");
    setPoolAddress(poolReceipt.contractAddress);
    pushLog(`vPool deployed: ${poolReceipt.contractAddress}`);

    // 3) Seed pool with tokens so exploit drains visibly (keep UI to 3 buttons)
    pushLog(`Seeding vPool with 1 token (internal step)…`);
    const { request: seedReq } = await client.simulateContract({
      address: tokenReceipt.contractAddress,
      abi: DummyTokenArtifact.abi,
      functionName: "transfer",
      args: [poolReceipt.contractAddress, parseEther("1")],
      account: address
    });
    const seedHash = await walletClient.writeContract(seedReq);
    pushLog(`Seed tx sent: ${seedHash}`);
    await client.waitForTransactionReceipt({ hash: seedHash });
    pushLog(`Seed confirmed.`);
  }

  async function onStep2DeployExploitContract() {
    await requireDifferentWalletThanStep1();
    await ensureChain(SCENARIO1_CHAIN.id);
    if (!walletClient) throw new Error("Wallet client not ready");
    const client = testnetPublicClient;
    if (!client) throw new Error("Public client not ready (testnet)");
    if (!address) throw new Error("No address");
    if (!tokenAddress || !poolAddress) throw new Error("Run step 1 first (deploy token + vPool)");

    pushLog(`Step 2: Deploying Attacker(token=${tokenAddress}, vPool=${poolAddress}) on ${SCENARIO1_CHAIN.name}…`);
    const hash = await walletClient.deployContract({
      abi: AttackerArtifact.abi,
      bytecode: AttackerArtifact.bytecode,
      args: [tokenAddress, poolAddress]
    });
    setAttackerDeployTx(hash);
    setStep2Deployer(address as Address);
    pushLog(`Attacker deploy tx sent: ${hash}`);

    const receipt = await client.waitForTransactionReceipt({ hash });
    if (!receipt.contractAddress) throw new Error("No attacker contractAddress in receipt");
    setAttackerAddress(receipt.contractAddress);
    pushLog(`Attacker deployed: ${receipt.contractAddress}`);
  }

  async function onStep3Exploit() {
    await requireDifferentWalletThanStep1();
    await ensureChain(SCENARIO1_CHAIN.id);
    if (!walletClient) throw new Error("Wallet client not ready");
    const client = testnetPublicClient;
    if (!client) throw new Error("Public client not ready (testnet)");
    if (!attackerAddress) throw new Error("Deploy attacker first (step 2)");
    if (!step2Deployer) throw new Error("Unknown attacker deployer (run step 2 again)");
    if (!address) throw new Error("No address");
    if (address.toLowerCase() !== step2Deployer.toLowerCase()) {
      throw new Error("Step 3 must be sent from the SAME wallet that deployed the attacker (owner). Switch back to that account.");
    }

    pushLog(`Step 3: Calling Attacker.attack()…`);
    const { request } = await client.simulateContract({
      address: attackerAddress,
      abi: AttackerArtifact.abi,
      functionName: "attack",
      args: [],
      account: address
    });
    const hash = await walletClient.writeContract(request);
    setAttackTx(hash);
    pushLog(`Attack tx sent: ${hash}`);
  }

  async function onScenario2DeployExploit() {
    await ensureBaseChain();
    const client = basePublicClient;
    if (!client) throw new Error("Public client not ready (Base)");
    if (!baseWalletClient) throw new Error("Wallet client not ready (Base)");
    if (!address) throw new Error("No address");

    // Build initCode (creation bytecode + constructor args) and deploy it via:
    // Multicall3.aggregate3Value([{ target: SingletonFactory, callData: deploy(initCode, salt) }])
    // Use the no-params attacker initcode (addresses are embedded in the bytecode).
    const initCode = encodeDeployData({
      abi: AttackerNoParamsArtifact.abi as any,
      bytecode: AttackerNoParamsArtifact.bytecode,
      args: []
    });

    const saltBytes = globalThis.crypto.getRandomValues(new Uint8Array(32));
    const salt = toHex(saltBytes) as `0x${string}`; // bytes32
    // Note: SingletonFactory uses CREATE2 under the hood, but we intentionally do NOT try
    // to predict/derive the address here. The workshop operator will paste the created address manually.
    pushLog(
      `Scenario 2 (Base): Deploying via Multicall3 → SingletonFactory\n` +
      `salt: ${salt}\n` +
      `After the tx is mined, copy the created contract address from the explorer/internal trace and paste it into the textbox below.`
    );

    const multicall3Abi = [
      {
        type: "function",
        name: "aggregate3Value",
        stateMutability: "payable",
        inputs: [
          {
            name: "calls",
            type: "tuple[]",
            components: [
              { name: "target", type: "address" },
              { name: "allowFailure", type: "bool" },
              { name: "value", type: "uint256" },
              { name: "callData", type: "bytes" }
            ]
          }
        ],
        outputs: [
          {
            name: "returnData",
            type: "tuple[]",
            components: [
              { name: "success", type: "bool" },
              { name: "returnData", type: "bytes" }
            ]
          }
        ]
      }
    ] as const;

    // IMPORTANT: Base's 0x4e59… factory is the minimal EIP-2470 deployer.
    // It does NOT implement an ABI function; it expects calldata = bytes32(salt) || initCode.
    const factoryCallData = concatHex([salt, initCode]);

    const multicallData = encodeFunctionData({
      abi: multicall3Abi,
      functionName: "aggregate3Value",
      args: [
        [
          {
            target: SINGLETON_FACTORY_ADDRESS,
            allowFailure: false,
            value: 0n,
            callData: factoryCallData
          }
        ]
      ]
    });

    pushLog(`Confirm in Coinbase Wallet…`);

    // Gas: this tx includes a full contract deployment inside Multicall3; wallet gas estimation can be too low.
    // We estimate on the public client and add a safety margin.
    let gas: bigint | undefined = undefined;
    try {
      const est = await client.estimateGas({
        account: address,
        to: MULTICALL3_ADDRESS,
        data: multicallData,
        value: 0n
      });
      gas = (est * 13n) / 10n; // +30%
      pushLog(`Scenario 2 gas estimate: ${est.toString()} (using ${gas.toString()})`);
    } catch (e) {
      gas = 2_500_000n;
      pushLog(`Scenario 2 gas estimate failed (${parseError(e)}). Using fallback gas=${gas.toString()}`);
    }

    const hash = await (baseWalletClient as any).sendTransaction({
      to: MULTICALL3_ADDRESS,
      data: multicallData,
      value: 0n,
      gas,
      account: address
    });

    setS2AttackerDeployTx(hash);
    setS2AttackerAddressInput("");
    pushLog(`Scenario 2 deployment tx sent: ${hash}`);
    await client.waitForTransactionReceipt({ hash });
    pushLog(`Scenario 2 deployment confirmed. Paste the created contract address into the textbox, then click Exploit.`);
  }

  async function onScenario2Exploit() {
    await ensureBaseChain();
    const client = basePublicClient;
    if (!client) throw new Error("Public client not ready (Base)");
    if (!baseWalletClient) throw new Error("Wallet client not ready (Base)");
    if (!address) throw new Error("No address");
    const target = s2AttackerAddressInput.trim();
    if (!isAddress(target)) throw new Error("Paste a valid attacker contract address first");

    const code = await client.getBytecode({ address: target as Address });
    if (!code) throw new Error("No bytecode at that address on Base (is it the created attacker contract?)");

    pushLog(`Scenario 2 (Base): Calling Attacker.attack()…`);
    const { request } = await client.simulateContract({
      address: target as Address,
      abi: AttackerArtifact.abi,
      functionName: "attack",
      args: [],
      account: address
    });
    const hash = await baseWalletClient.writeContract(request);
    setS2AttackTx(hash);
    pushLog(`Scenario 2 attack tx sent: ${hash}`);
  }

  const tokenTxUrl = explorerTxUrl(chainForLinks, tokenDeployTx);
  const tokenAddrUrl = explorerAddressUrl(chainForLinks, tokenAddress);
  const poolTxUrl = explorerTxUrl(chainForLinks, poolDeployTx);
  const poolAddrUrl = explorerAddressUrl(chainForLinks, poolAddress);
  const attackerTxUrl = explorerTxUrl(chainForLinks, attackerDeployTx);
  const attackerAddrUrl = explorerAddressUrl(chainForLinks, attackerAddress);
  const attackTxUrl = explorerTxUrl(chainForLinks, attackTx);

  const s2Chain = base;
  const s2AttackerTxUrl = explorerTxUrl(s2Chain, s2AttackerDeployTx);
  const s2AttackerAddrUrl = isAddress(s2AttackerAddressInput.trim())
    ? explorerAddressUrl(s2Chain, s2AttackerAddressInput.trim() as Address)
    : undefined;
  const s2AttackTxUrl = explorerTxUrl(s2Chain, s2AttackTx);
  const s2VpoolAddrUrl = explorerAddressUrl(s2Chain, SCENARIO2_VPOOL);

  function onSubmitPassword() {
    if (passwordInput !== requiredPassword) throw new Error("Wrong password");
    try {
      sessionStorage.setItem("bny_workshop_authed", "1");
    } catch {
      // ignore
    }
    setIsAuthed(true);
    setPasswordInput("");
  }

  return (
    <div className="container">
      {!isAuthed ? (
        <div className="card" style={{ maxWidth: 520, margin: "60px auto 0" }}>
          <div className="sectionTitle">Password required</div>
          <div className="muted" style={{ fontSize: 14, lineHeight: 1.5 }}>
            This workshop dapp is protected with a simple password gate.
            <br />

          </div>
          <div style={{ height: 12 }} />
          <div className="row">
            <div style={{ flex: 1, minWidth: 260 }}>
              <div className="label">Password</div>
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder="Enter password"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    try {
                      onSubmitPassword();
                    } catch (err) {
                      // eslint-disable-next-line no-console
                      console.error(err);
                    }
                  }
                }}
              />
            </div>
            <button
              onClick={() => {
                try {
                  onSubmitPassword();
                } catch (e) {
                  // eslint-disable-next-line no-console
                  console.error(e);
                  // keep UX simple: just log; event log is behind the gate
                  alert("Wrong password");
                }
              }}
            >
              Enter
            </button>
          </div>
        </div>
      ) : null}

      {isAuthed ? (
        <>
          <div className="header">
            <div>
              <div className="title">BNY Workshop Dapp (Coinbase Wallet)</div>
              <div className="subtitle">
                Scenario 1 only: deploy Token + vulnerable vPool, deploy Attacker, then exploit — all from the dapp.
                <br />
                Important: steps <span className="mono">2</span> and <span className="mono">3</span> must be sent from a <span className="danger">different wallet address</span>{" "}
                than step <span className="mono">1</span>.
              </div>
            </div>

            <div className="card">
              <div className="row" style={{ justifyContent: "flex-end" }}>
                <button
                  className="btnDanger"
                  onClick={() => {
                    try {
                      sessionStorage.removeItem("bny_workshop_authed");
                    } catch {
                      // ignore
                    }
                    setIsAuthed(false);
                  }}
                >
                  Logout
                </button>
                {isConnected ? (
                  <>
                    <span className="pill">
                      <span>Connected</span>
                      <span className="mono">{shortAddr(address)}</span>
                    </span>
                    <span className="pill">
                      <span>Chain</span>
                      <span className="mono">{currentChain?.name ?? `#${chainId}`}</span>
                    </span>
                    <button className="btnDanger" onClick={() => disconnect()}>
                      Disconnect
                    </button>
                  </>
                ) : (
                  <button onClick={onConnectCoinbase} disabled={isConnecting}>
                    Connect Coinbase Wallet
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="grid">
            <div className="card cardPanel">
              <div className="sectionTitle">Scenario 1 — Testnet</div>

              <div className="pill" style={{ marginBottom: 12 }}>
                Step 0 (operator): onboard vPool on Base to the platform —{" "}
                <span className="mono">{SCENARIO2_VPOOL}</span>
                {s2VpoolAddrUrl ? (
                  <a href={s2VpoolAddrUrl} target="_blank" rel="noreferrer">
                    Base explorer
                  </a>
                ) : null}
              </div>

              <div className="pill">
                Scenario 1 chain is locked to <span className="mono">{SCENARIO1_CHAIN.name}</span> (chainId={SCENARIO1_CHAIN.id})
              </div>

              <div style={{ height: 12 }} />

              <div className="row btnRow">
                <button onClick={() => onStep1DeployTokenAndPool().catch((e) => pushLog(`Step 1 error: ${parseError(e)}`))} disabled={!isConnected}>
                  1) Deploy Token + vPool (testnet)
                </button>
                <button
                  onClick={() => onStep2DeployExploitContract().catch((e) => pushLog(`Step 2 error: ${parseError(e)}`))}
                  disabled={!isConnected || !tokenAddress || !poolAddress}
                  title="Must be a different wallet address than step 1"
                >
                  2) Deploy Exploit (Attacker)
                </button>
                <button
                  onClick={() => onStep3Exploit().catch((e) => pushLog(`Step 3 error: ${parseError(e)}`))}
                  disabled={!isConnected || !attackerAddress}
                  title="Must be the same wallet address that deployed the attacker"
                >
                  3) Exploit (attack)
                </button>
              </div>

              <div style={{ height: 12 }} />

              <div className="row">
                {tokenAddress ? (
                  <span className="pill">
                    Token: <span className="mono">{shortAddr(tokenAddress)}</span>
                    {tokenAddrUrl ? (
                      <a href={tokenAddrUrl} target="_blank" rel="noreferrer">
                        explorer
                      </a>
                    ) : null}
                    {tokenTxUrl ? (
                      <a href={tokenTxUrl} target="_blank" rel="noreferrer">
                        deploy tx
                      </a>
                    ) : null}
                  </span>
                ) : null}
                {poolAddress ? (
                  <span className="pill">
                    vPool: <span className="mono">{shortAddr(poolAddress)}</span>
                    {poolAddrUrl ? (
                      <a href={poolAddrUrl} target="_blank" rel="noreferrer">
                        explorer
                      </a>
                    ) : null}
                    {poolTxUrl ? (
                      <a href={poolTxUrl} target="_blank" rel="noreferrer">
                        deploy tx
                      </a>
                    ) : null}
                  </span>
                ) : null}
              </div>

              <div style={{ height: 10 }} />

              <div className="row">
                {attackerAddress ? (
                  <span className="pill">
                    Attacker: <span className="mono">{shortAddr(attackerAddress)}</span>
                    {attackerAddrUrl ? (
                      <a href={attackerAddrUrl} target="_blank" rel="noreferrer">
                        explorer
                      </a>
                    ) : null}
                    {attackerTxUrl ? (
                      <a href={attackerTxUrl} target="_blank" rel="noreferrer">
                        deploy tx
                      </a>
                    ) : null}
                  </span>
                ) : null}
                {attackTx ? (
                  <span className="pill">
                    Attack tx: <span className="mono">{shortAddr(attackTx)}</span>
                    {attackTxUrl ? (
                      <a href={attackTxUrl} target="_blank" rel="noreferrer">
                        explorer
                      </a>
                    ) : null}
                  </span>
                ) : null}
              </div>

              <div style={{ height: 12 }} />
              <button onClick={() => setShowCode(true)}>Show contracts code</button>
            </div>

            <div className="card cardPanel">
              <div className="sectionTitle">Scenario 2 — Base (force Base chain)</div>
              <div className="muted" style={{ fontSize: 14, lineHeight: 1.5 }}>
                These actions always run on <span className="mono">Base</span>.
                <br />
                Token: <span className="mono">{SCENARIO2_TOKEN}</span>
                <br />
                vPool: <span className="mono">{SCENARIO2_VPOOL}</span>
              </div>

              <div style={{ height: 12 }} />

              <div className="row btnRow">
                <button onClick={() => onScenario2DeployExploit().catch((e) => pushLog(`Scenario 2 deploy error: ${parseError(e)}`))} disabled={!isConnected}>
                  1) Deploy exploit contract (Base)
                </button>
                <button
                  onClick={() => onScenario2Exploit().catch((e) => pushLog(`Scenario 2 exploit error: ${parseError(e)}`))}
                  disabled={!isConnected || !isAddress(s2AttackerAddressInput.trim())}
                >
                  2) Exploit (attack)
                </button>
              </div>

              <div style={{ height: 12 }} />

              <div className="row">
                <div style={{ flex: 1, minWidth: 320 }}>
                  <div className="label">Attacker contract address (paste after deploy)</div>
                  <input value={s2AttackerAddressInput} onChange={(e) => setS2AttackerAddressInput(e.target.value)} placeholder="0x…" />
                </div>
              </div>

              <div style={{ height: 12 }} />

              <div className="row">
                {s2AttackerAddrUrl ? (
                  <span className="pill">
                    Scenario 2 Attacker: <span className="mono">{shortAddr(s2AttackerAddressInput.trim())}</span>
                    <a href={s2AttackerAddrUrl} target="_blank" rel="noreferrer">
                      explorer
                    </a>
                    {s2AttackerTxUrl ? (
                      <a href={s2AttackerTxUrl} target="_blank" rel="noreferrer">
                        deploy tx
                      </a>
                    ) : null}
                  </span>
                ) : null}
                {s2AttackTx ? (
                  <span className="pill">
                    Scenario 2 attack tx: <span className="mono">{shortAddr(s2AttackTx)}</span>
                    {s2AttackTxUrl ? (
                      <a href={s2AttackTxUrl} target="_blank" rel="noreferrer">
                        explorer
                      </a>
                    ) : null}
                  </span>
                ) : null}
              </div>

              <div style={{ height: 14 }} />

              <div className="sectionTitle">Notes</div>
              <div className="muted" style={{ fontSize: 14, lineHeight: 1.5 }}>
                - Scenario 1 step 1 also <span className="mono">seeds the pool</span> with 1 token internally (so the exploit drains something visible) while keeping the UI at 3
                buttons.
                <br />- After Scenario 1 step 1: switch to a different Coinbase Wallet account before clicking Scenario 1 steps 2/3.
              </div>
            </div>
          </div>

          <div style={{ height: 14 }} />

          <div className="card">
            <div className="sectionTitle">Event log</div>
            <div className="muted" style={{ fontSize: 13, lineHeight: 1.4 }}>
              This log is your “script”: copy/paste tx hashes into the platform, and use the explorer links to show “testnet deployment tx” for the incidents.
            </div>
            <div style={{ height: 10 }} />
            <div className="log">{log || "No actions yet."}</div>
          </div>

          {showCode ? (
            <div className="modalOverlay" onClick={() => setShowCode(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <div className="row" style={{ justifyContent: "space-between" }}>
                  <div className="sectionTitle" style={{ margin: 0 }}>
                    Contracts source code
                  </div>
                  <button className="btnDanger" onClick={() => setShowCode(false)}>
                    Close
                  </button>
                </div>

                <div style={{ height: 10 }} />

                <div className="tabs">
                  <button className={codeTab === "token" ? "tab tabActive" : "tab"} onClick={() => setCodeTab("token")}>
                    token.sol
                  </button>
                  <button className={codeTab === "vpool" ? "tab tabActive" : "tab"} onClick={() => setCodeTab("vpool")}>
                    vpool.sol
                  </button>
                  <button className={codeTab === "attacker" ? "tab tabActive" : "tab"} onClick={() => setCodeTab("attacker")}>
                    attacker.sol
                  </button>
                </div>

                <div style={{ height: 10 }} />

                <div className="codeBox">{ContractSources[codeTab]}</div>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}



